import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { ApiService } from 'ish-core/services/api/api.service';

import {
  clearPresidioMockImpersonationSession,
  getPresidioMockCsrSearchCatalog,
  getPresidioMockImpersonatedDisplayName,
  getPresidioMockImpersonatedLogin,
  isPresidioMockImpersonating,
  searchPresidioMockCsrUsers,
  setPresidioMockImpersonatedLogin,
  type PresidioMockCsrSearchUser,
} from '../mock/presidio-mock-session';

/** PRODUCTION-INTENT shape — demo uses LOCAL_MOCK session + provisional REST. */
export interface PresidioImpersonationCandidate {
  login: string;
  username: string;
  email: string;
  displayName: string;
  firstName: string;
  lastName: string;
}

export interface PresidioImpersonationState {
  impersonating: boolean;
  login: string;
  displayName: string;
}

/**
 * DEMO MVP 8.2.2 — CSR impersonation start/end + searchable end-user catalog.
 * Session helpers are the LOCAL_MOCK source of truth; REST notifies the mock interceptor.
 */
@Injectable({ providedIn: 'root' })
export class PresidioImpersonationService {
  private apiService = inject(ApiService);

  searchCandidates(query: string, limit = 50): Observable<PresidioImpersonationCandidate[]> {
    return of(searchPresidioMockCsrUsers(query, limit).map(toCandidate));
  }

  getState(): PresidioImpersonationState {
    const login = getPresidioMockImpersonatedLogin();
    return {
      impersonating: !!login,
      login,
      displayName: getPresidioMockImpersonatedDisplayName(),
    };
  }

  start(login: string): Observable<PresidioImpersonationState> {
    const next = (login || '').trim().toLowerCase();
    const target = getPresidioMockCsrSearchCatalog().find(
      t => t.login.toLowerCase() === next || t.username.toLowerCase() === next
    );
    if (!target || !setPresidioMockImpersonatedLogin(target.login, target.displayName)) {
      return of(this.getState());
    }
    return this.apiService.post<string>('customers/-/users/-/impersonate', { login: target.login }).pipe(
      map(() => this.getState()),
      catchError(() => of(this.getState()))
    );
  }

  end(): Observable<PresidioImpersonationState> {
    clearPresidioMockImpersonationSession();
    return this.apiService.post<string>('customers/-/users/-/impersonate/end', {}).pipe(
      map(() => this.getState()),
      catchError(() => of(this.getState()))
    );
  }

  isImpersonating(): boolean {
    return isPresidioMockImpersonating();
  }
}

function toCandidate(user: PresidioMockCsrSearchUser): PresidioImpersonationCandidate {
  return {
    login: user.login,
    username: user.username,
    email: user.login,
    displayName: user.displayName,
    firstName: user.firstName,
    lastName: user.lastName,
  };
}
